import { useState } from "react";
import { Check, CornerDownRight, Loader2, Sparkles, X } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import type { Collaboration, RateOffer } from "@/hooks/useCollaborations";
import CommissionSplit from "./CommissionSplit";

/**
 * The rate negotiation as a vertical history.
 *
 * `RateOffer.parentOfferId` models this as a chain rather than a mutable
 * column, so it is rendered as one: who offered what, when, each counter
 * indented under the offer it answers. Both sides see the identical history —
 * that is the point, and it is what a WhatsApp thread and a spreadsheet cannot
 * give an agency reconstructing a negotiation weeks later.
 */
export default function OfferChain({
  collaboration,
  onOffer,
  onAccept,
  onReject,
  busy,
}: {
  collaboration: Collaboration;
  onOffer: (amount: number, message?: string) => Promise<void>;
  onAccept: (offerId: string) => Promise<void>;
  onReject: (offerId: string) => Promise<void>;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { offers, pendingOffer, viewerParty, money, status } = collaboration;
  // Non-null from the server: the collaboration's frozen rate, or the
  // campaign's before acceptance. A literal default here would be wrong for
  // any account not on the standard rate.
  const commissionPct = money.commissionPct;

  const negotiating = status === "NEGOTIATING";
  // You cannot answer your own offer, so the actions only appear when the live
  // offer came from the other side. The server enforces this too.
  const canAnswer = negotiating && pendingOffer != null && pendingOffer.offeredBy !== viewerParty;
  const canOffer = negotiating && (pendingOffer == null || pendingOffer.offeredBy !== viewerParty);

  const submitOffer = async () => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setError(t("marketplace.collaboration.offer.invalidAmount"));
      return;
    }

    setError(null);
    try {
      await onOffer(value, message.trim() || undefined);
      setAmount("");
      setMessage("");
    } catch (e) {
      setError(e instanceof Error ? e.message : t("marketplace.collaboration.offer.failed"));
    }
  };

  const answer = async (action: () => Promise<void>) => {
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("marketplace.collaboration.offer.failed"));
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-semibold text-neutral-900">
        {t("marketplace.collaboration.offer.title")}
      </h2>

      {offers.length === 0 ? (
        <p className="text-sm text-neutral-400">
          {t("marketplace.collaboration.offer.empty")}
        </p>
      ) : (
        <ol className="space-y-2">
          {offers.map((offer) => (
            <OfferRow
              key={offer.id}
              offer={offer}
              viewerParty={viewerParty}
              commissionPct={commissionPct}
              isCounter={offer.parentOfferId !== null}
            />
          ))}
        </ol>
      )}

      {canAnswer && pendingOffer && (
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            disabled={busy}
            onClick={() => answer(() => onAccept(pendingOffer.id))}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-button bg-gradient-to-r from-steel-600 to-steel-700 text-white text-xs font-medium hover:from-steel-500 hover:to-steel-600 transition-all cursor-pointer disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {t("marketplace.collaboration.offer.accept")}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => answer(() => onReject(pendingOffer.id))}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-control border border-neutral-200 text-neutral-600 text-xs font-medium hover:bg-neutral-50 transition-colors cursor-pointer disabled:opacity-40"
          >
            <X className="w-3.5 h-3.5" />
            {t("marketplace.collaboration.offer.reject")}
          </button>
        </div>
      )}

      {canOffer && (
        <div className="pt-3 border-t border-neutral-100 space-y-2">
          <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-widest">
            {offers.length === 0
              ? t("marketplace.collaboration.offer.makeFirst")
              : t("marketplace.collaboration.offer.counter")}
          </p>

          <div className="flex items-center gap-2">
            <span className="text-xs text-neutral-500 flex-shrink-0">{money.currency}</span>
            <input
              type="number"
              min="0"
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={t("marketplace.collaboration.offer.amountPlaceholder")}
              className="flex-1 min-w-0 px-3 py-2 rounded-control border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-steel-600/20 focus:border-steel-600"
            />
          </div>

          <textarea
            rows={2}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("marketplace.collaboration.offer.messagePlaceholder")}
            className="w-full px-3 py-2 rounded-control border border-neutral-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-steel-600/20 focus:border-steel-600"
          />

          {/* What the number actually means for both sides, before it is sent
              rather than after it is agreed. */}
          {Number(amount) > 0 && (
            <div className="px-3 py-2 rounded-control bg-neutral-50 border border-neutral-100">
              <CommissionSplit
                rate={Number(amount)}
                currency={money.currency}
                commissionPct={commissionPct}
                viewerParty={viewerParty}
                compact
              />
            </div>
          )}

          <button
            type="button"
            disabled={busy || !amount}
            onClick={submitOffer}
            className="w-full px-3 py-2 rounded-button bg-gradient-to-r from-steel-600 to-steel-700 text-white text-xs font-medium hover:from-steel-500 hover:to-steel-600 transition-all cursor-pointer disabled:opacity-40"
          >
            {busy ? t("common.loading") : t("marketplace.collaboration.offer.send")}
          </button>
        </div>
      )}

      {error && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-control px-3 py-2">
          {error}
        </p>
      )}
    </div>
  );
}

function OfferRow({
  offer,
  viewerParty,
  commissionPct,
  isCounter,
}: {
  offer: RateOffer;
  viewerParty: "BRAND" | "CREATOR";
  commissionPct: number;
  isCounter: boolean;
}) {
  const { t } = useTranslation();
  const mine = offer.offeredBy === viewerParty;

  const statusTone: Record<RateOffer["status"], string> = {
    PENDING: "bg-amber-50 text-amber-700 border-amber-200",
    ACCEPTED: "bg-emerald-50 text-emerald-700 border-emerald-200",
    REJECTED: "bg-red-50 text-red-600 border-red-200",
    COUNTERED: "bg-neutral-100 text-neutral-500 border-neutral-200",
    EXPIRED: "bg-neutral-100 text-neutral-400 border-neutral-200",
  };

  return (
    <li className={isCounter ? "ml-4" : ""}>
      <div className="flex items-start gap-2">
        {isCounter && (
          <CornerDownRight className="w-3.5 h-3.5 text-neutral-300 flex-shrink-0 mt-2.5" />
        )}

        <div className="flex-1 min-w-0 border border-neutral-200 rounded-control p-3 bg-white">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-neutral-500">
              {mine
                ? t("marketplace.collaboration.offer.byYou")
                : offer.offeredBy === "BRAND"
                  ? t("marketplace.collaboration.offer.byBrand")
                  : t("marketplace.collaboration.offer.byCreator")}
            </span>
            <span
              className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${statusTone[offer.status]}`}
            >
              {t(`marketplace.collaboration.offerStatus.${offer.status}`)}
            </span>
          </div>

          <p className="text-base font-bold text-neutral-900 mt-1 tabular-nums">
            {offer.currency} {offer.amount.toLocaleString("es")}
          </p>

          <div className="mt-2 pt-2 border-t border-neutral-100">
            <CommissionSplit
              rate={offer.amount}
              currency={offer.currency}
              commissionPct={commissionPct}
              viewerParty={viewerParty}
              compact
            />
          </div>

          {offer.message && (
            <p className="text-xs text-neutral-600 mt-2 leading-relaxed">{offer.message}</p>
          )}

          {/* The agent proposes, the human decides — so when a rate came from
              the assistant, say so and show why. */}
          {offer.aiSuggested && offer.aiRationale && (
            <p className="flex items-start gap-1.5 text-[11px] text-steel-700 mt-2 leading-relaxed">
              <Sparkles className="w-3 h-3 flex-shrink-0 mt-0.5" />
              {offer.aiRationale}
            </p>
          )}

          <p className="text-[10px] text-neutral-400 mt-2">
            {new Date(offer.createdAt).toLocaleString("es", {
              day: "numeric",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </div>
      </div>
    </li>
  );
}
